import type {
	CalendarColorBy,
	CalendarDayStart,
	CalendarIssueEventFields,
	CalendarScopeBoard,
	CalendarViewRange,
	CalendarWeekStart,
} from '@atlassian/jira-calendar/src/controllers/calendar-store/types.tsx';
import type { EntryPointRouteParams } from '@atlassian/jira-entry-points-plugin/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import parameters from '@atlassian/jira-relay/src/__generated__/CalendarContentQuery$parameters';
import type {
	JiraViewScopeInput,
	JiraCalendarViewConfigurationInput,
	JiraCalendarIssuesInput,
	JiraCalendarVersionsInput,
	JiraCalendarSprintsInput,
	JiraCalendarMode,
	JiraCalendarWeekStart,
} from '@atlassian/jira-relay/src/__generated__/CalendarContentQuery.graphql';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

// This is the key used to store the calendar filters in the local storage
const ISSUE_SEARCH_INPUT_STORAGE_KEY = 'calendar-issue-search-input';
// This is the key used to store the calendar view settings in the local storage
const VIEW_SETTINGS_STORAGE_KEY = 'calendar-view-settings';

/**
 * Default Day Starts At Time in Calendar View Settings
 */
const DEFAULT_DAY_STARTS_AT: CalendarDayStart = '09:00 AM';

function toBoardAri({ siteId, boardId }: { siteId: string; boardId: string }) {
	return `ari:cloud:jira-software:${siteId}:board/${boardId}`;
}

function toMidnightUTCString(date: Date) {
	const utcMilliseconds = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
	return new Date(utcMilliseconds).toISOString();
}

function toDate(date: string | null | undefined): Date {
	if (!date) {
		return new Date();
	}
	try {
		const _date = new Date(date);
		return new Date(_date.getFullYear(), _date.getMonth(), _date.getDate());
	} catch (e) {
		return new Date();
	}
}

function createScope(
	{ context, tenantContext }: EntryPointRouteParams,
	viewId: string,
): CalendarScopeBoard {
	const { cloudId } = tenantContext;

	return {
		type: 'board',
		boardId: toBoardAri({
			siteId: cloudId,
			boardId: String(context.match.params.boardId),
		}),
		projectKeys: fg('fix_jsm_calendar_scope_issue')
			? []
			: [String(context.match.params.projectKey)],
		viewId,
	};
}

function buildScopeInput(
	{ tenantContext }: EntryPointRouteParams,
	scope: CalendarScopeBoard,
): JiraViewScopeInput {
	const { cloudId } = tenantContext;
	return {
		ids: [scope.boardId],
		projectKeys: {
			cloudId,
			keys: scope.projectKeys,
		},
	};
}

function toViewMode(mode: string): JiraCalendarMode {
	switch (mode) {
		case 'week':
			return 'WEEK';
		case 'day':
			return 'DAY';
		default:
			return 'MONTH';
	}
}

function toWeekStart(weekStart: string): JiraCalendarWeekStart {
	switch (weekStart) {
		case 'monday':
			return 'MONDAY';
		case 'saturday':
			return 'SATURDAY';
		default:
			return 'SUNDAY';
	}
}

function buildConfigurationInput({
	selectedDate,
	viewRange,
	weekStartsOn,
	startDateField,
	endDateField,
	viewId,
}: {
	selectedDate: Date;
	viewRange: CalendarViewRange;
	weekStartsOn: CalendarWeekStart;
	startDateField: string;
	endDateField: string;
	viewId: string | null;
}): JiraCalendarViewConfigurationInput {
	return {
		date: toMidnightUTCString(selectedDate),
		mode: toViewMode(viewRange),
		weekStart: toWeekStart(weekStartsOn),
		...(fg('jira_calendar_load_more_pagination') ? { startDateField, endDateField } : {}),
		viewId,
	};
}

function buildStorageKey({
	cloudId,
	activationId,
	boardId,
	featureKey,
}: {
	cloudId: string;
	activationId: string;
	boardId: string;
	featureKey: string;
}): string {
	const viewId = buildViewId({ cloudId, activationId, boardId });
	const LOCALSTORAGE_KEY_PREFIX = 'persist:calendar';
	return `${LOCALSTORAGE_KEY_PREFIX}/view/${viewId}/${featureKey}`;
}

function getStorageValue(storageKey: string): string {
	// window check is needed because this code is also executed on the SSR
	if (typeof window !== 'undefined') {
		return localStorage?.getItem(storageKey) || '';
	}
	return '';
}

function buildIssuesSearchInput(storageKey: string): JiraCalendarIssuesInput {
	return { additionalFilterQuery: getStorageValue(storageKey) || '' };
}

function buildUnscheduledIssuesSearchInput(): JiraCalendarIssuesInput {
	return { additionalFilterQuery: '' };
}

function buildVersionsSearchInput(): JiraCalendarVersionsInput {
	return { versionStatuses: ['RELEASED', 'UNRELEASED'] };
}

function buildSprintsSearchInput(): JiraCalendarSprintsInput {
	return { sprintStates: ['ACTIVE', 'FUTURE', 'CLOSED'] };
}

function buildViewId({
	cloudId,
	activationId,
	boardId,
}: {
	cloudId: string;
	activationId: string;
	boardId: string;
}): string {
	// https://developer.atlassian.com/platform/atlassian-resource-identifier/resource-owners/registry/#ati%3Acloud%3Ajira%3Aview
	return `ari:cloud:jira:${cloudId}:view/activation/${activationId}/board/${boardId}`;
}

export const calendarEntryPoint = createEntryPoint({
	root: JSResourceForUserVisible(
		() =>
			import(
				/* webpackChunkName: "async-servicedesk-calendar", jiraSpaEntry: "async-servicedesk-calendar" */ './src'
			),
	),
	getPreloadProps: (entryPointRouteParams: EntryPointRouteParams) => {
		const { context, tenantContext } = entryPointRouteParams;
		const { cloudId, activationId } = tenantContext;
		const { date } = context.query;
		const selectedDate = toDate(date);
		const { boardId, projectKey } = context.match.params;
		if (!boardId || !projectKey) {
			throw new Error('boardId and projectKey must be provided.');
		}
		const viewId = buildViewId({ cloudId, activationId, boardId });
		const scope = createScope(entryPointRouteParams, viewId || '');
		const storageKeyForIssueSearchInput = buildStorageKey({
			cloudId,
			activationId,
			boardId,
			featureKey: ISSUE_SEARCH_INPUT_STORAGE_KEY,
		});
		const storageKeyForViewSettings = buildStorageKey({
			cloudId,
			activationId,
			boardId,
			featureKey: VIEW_SETTINGS_STORAGE_KEY,
		});
		const defaultViewRange: CalendarViewRange = 'month';
		const storedConfig = getStorageValue(storageKeyForViewSettings);
		const viewSettingsConfig = storedConfig
			? JSON.parse(storedConfig)
			: {
					viewRange: defaultViewRange,
					weekStartsOn: null,
					dayStartsAt: DEFAULT_DAY_STARTS_AT,
					showWeekends: false,
					showIssueKey: true,
					showIn24HourClock: false,
					colorBy: 'status',
				};
		const viewRange: CalendarViewRange = viewSettingsConfig.viewRange ?? 'month';
		const weekStartsOn: CalendarWeekStart = viewSettingsConfig.weekStartsOn ?? 'sunday';
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const colorBy: CalendarColorBy = (viewSettingsConfig.colorBy as CalendarColorBy) ?? 'status';
		const startDateField = 'startdate';
		const endDateField = 'duedate';
		const issueEventFields: CalendarIssueEventFields = {
			startDateField,
			endDateField,
		};
		const issuesSearchInput = buildIssuesSearchInput(storageKeyForIssueSearchInput);
		return {
			queries: {
				calendarData: {
					options: {
						fetchPolicy: 'network-only' as const,
					},
					parameters,
					variables: {
						cloudId,
						scopeInput: buildScopeInput(entryPointRouteParams, scope),
						configurationInput: buildConfigurationInput({
							selectedDate,
							viewRange,
							weekStartsOn,
							startDateField,
							endDateField,
							viewId,
						}),
						issuesPageSize: 200,
						issuesSearchInput,
						unscheduledIssuesSearchInput: buildUnscheduledIssuesSearchInput(),
						versionsSearchInput: buildVersionsSearchInput(),
						sprintsSearchInput: buildSprintsSearchInput(),
						skipSprintSearch: false,
						viewId,
						skipFieldConfig: false,
						pageLoadMoreEnabled: fg('jira_calendar_load_more_pagination'),
						skipVersionsV2Search: false,
						includeRecurrenceIndicator: false,
						pageLoadMoreEnabledViewSettings: fg('jira_calendar_load_more_pagination_view_settings'),
						skipUserPreferences: false,
					},
				},
			},
			entryPoints: {},
			extraProps: {
				scope,
				selectedDate,
				viewRange,
				weekStartsOn,
				dayStartsAt: viewSettingsConfig.dayStartsAt,
				issueEventFields,
				projectKey,
				storageKeyForViewSettings,
				storageKeyForIssueSearchInput,
				showWeekends: viewSettingsConfig.showWeekends,
				showIssueKey: viewSettingsConfig.showIssueKey,
				showIn24HourClock: viewSettingsConfig.showIn24HourClock,
				colorBy,
			},
		};
	},
});
